import { InAppNotification, RecipeComment, RecipeRealization } from '../api/Types';
import { Author } from '../api/Author';
import { Recipe } from '../api/Recipe';
import { storageService } from './StorageService';
import { normalizeAuthorRelation, type RelationValue } from './serviceHelpers';

interface NotificationActorShape {
  id: number;
  name: string;
  first_name: string;
  avatar_url: string | null;
}

interface NotificationRowShape {
  id: number;
  event_type: InAppNotification['event_type'];
  recipe_id: string | null;
  comment_id: number | null;
  realization_id: number | null;
  title: string;
  body: string | null;
  route_path: string;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  actor: RelationValue<NotificationActorShape>;
}

type ImageUrlTransformer = (url: string | null | undefined) => string | null;

export interface ServiceMappers {
  normalizeAuthorAvatar: <T extends Pick<Author, 'avatar_url'>>(author: T) => T;
  normalizeOptionalAuthorAvatar: <T extends Pick<Author, 'avatar_url'> | null | undefined>(author: T) => T;
  normalizePublicRecipe: <T extends Pick<Recipe, 'image' | 'authors'>>(recipe: T) => T;
  normalizePublicRecipes: <T extends Pick<Recipe, 'image' | 'authors'>>(recipes: T[]) => T[];
  normalizeRecipeComment: <T extends Pick<RecipeComment, 'author'>>(comment: T) => T;
  normalizeRecipeComments: <T extends Pick<RecipeComment, 'author'>>(comments: T[]) => T[];
  normalizeRecipeRealization: <T extends Pick<RecipeRealization, 'image' | 'author'>>(realization: T) => T;
  normalizeRecipeRealizations: <T extends Pick<RecipeRealization, 'image' | 'author'>>(realizations: T[]) => T[];
  mapNotificationRow: <T extends NotificationRowShape>(row: T) => InAppNotification;
  mapNotificationRows: <T extends NotificationRowShape>(rows: T[]) => InAppNotification[];
}

export const createServiceMappers = (
  transformImageUrl: ImageUrlTransformer
): ServiceMappers => {
  const normalizeAuthorAvatar = <T extends Pick<Author, 'avatar_url'>>(author: T): T => ({
    ...author,
    avatar_url: transformImageUrl(author.avatar_url),
  });

  const normalizeOptionalAuthorAvatar = <T extends Pick<Author, 'avatar_url'> | null | undefined>(
    author: T
  ): T => {
    if (!author) {
      return author;
    }

    return normalizeAuthorAvatar(author);
  };

  const normalizePublicRecipe = <T extends Pick<Recipe, 'image' | 'authors'>>(recipe: T): T => ({
    ...recipe,
    image: transformImageUrl(recipe.image),
    authors: normalizeOptionalAuthorAvatar(recipe.authors),
  });

  const normalizePublicRecipes = <T extends Pick<Recipe, 'image' | 'authors'>>(recipes: T[]): T[] =>
    recipes.map((recipe) => normalizePublicRecipe(recipe));

  const normalizeRecipeComment = <T extends Pick<RecipeComment, 'author'>>(comment: T): T => ({
    ...comment,
    author: normalizeOptionalAuthorAvatar(comment.author),
  });

  const normalizeRecipeComments = <T extends Pick<RecipeComment, 'author'>>(comments: T[]): T[] =>
    comments.map((comment) => normalizeRecipeComment(comment));

  const normalizeRecipeRealization = <T extends Pick<RecipeRealization, 'image' | 'author'>>(
    realization: T
  ): T => ({
    ...realization,
    image: transformImageUrl(realization.image),
    author: normalizeOptionalAuthorAvatar(realization.author),
  });

  const normalizeRecipeRealizations = <T extends Pick<RecipeRealization, 'image' | 'author'>>(
    realizations: T[]
  ): T[] => realizations.map((realization) => normalizeRecipeRealization(realization));

  const mapNotificationRow = <T extends NotificationRowShape>(row: T): InAppNotification => {
    const actor = normalizeOptionalAuthorAvatar(normalizeAuthorRelation(row.actor));

    return {
      id: row.id,
      event_type: row.event_type,
      recipe_id: row.recipe_id,
      comment_id: row.comment_id,
      realization_id: row.realization_id,
      title: row.title,
      body: row.body,
      route_path: row.route_path,
      is_read: row.is_read,
      read_at: row.read_at,
      created_at: row.created_at,
      actor,
    };
  };

  const mapNotificationRows = <T extends NotificationRowShape>(rows: T[]): InAppNotification[] =>
    rows.map((row) => mapNotificationRow(row));

  return {
    normalizeAuthorAvatar,
    normalizeOptionalAuthorAvatar,
    normalizePublicRecipe,
    normalizePublicRecipes,
    normalizeRecipeComment,
    normalizeRecipeComments,
    normalizeRecipeRealization,
    normalizeRecipeRealizations,
    mapNotificationRow,
    mapNotificationRows,
  };
};

export const serviceMappers = createServiceMappers((url) => storageService.transformImageUrl(url));

export const normalizeAuthorAvatar = serviceMappers.normalizeAuthorAvatar;
export const normalizeOptionalAuthorAvatar = serviceMappers.normalizeOptionalAuthorAvatar;
export const normalizePublicRecipe = serviceMappers.normalizePublicRecipe;
export const normalizePublicRecipes = serviceMappers.normalizePublicRecipes;
export const normalizeRecipeComment = serviceMappers.normalizeRecipeComment;
export const normalizeRecipeComments = serviceMappers.normalizeRecipeComments;
export const normalizeRecipeRealization = serviceMappers.normalizeRecipeRealization;
export const normalizeRecipeRealizations = serviceMappers.normalizeRecipeRealizations;
export const mapNotificationRow = serviceMappers.mapNotificationRow;
export const mapNotificationRows = serviceMappers.mapNotificationRows;
